import { useEffect, useRef, useState } from 'react';
import type { VavClient, UserSummary } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { PhoneField } from './PhoneField.tsx';
import { useT } from '../i18n.tsx';
import { InvalidPhoneError } from '@vav/client-core';

interface Props {
  client: VavClient;
  onClose: () => void;
  onOpened: (conversationId: string) => void;
}

/** Chrome on Android exposes a contact picker; nothing else does yet. */
function contactPickerAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'contacts' in navigator && 'ContactsManager' in window;
}

/**
 * Phone-number discovery.
 *
 * Two halves: publish your own number so friends can find you, and look up
 * theirs. Numbers are hashed on this device — the server only ever sees the
 * hash, and only for numbers you actually look up.
 */
export function PhoneDialog({ client, onClose, onOpened }: Props) {
  const t = useT();
  const [myPhone, setMyPhone] = useState('');
  const [saved, setSaved] = useState(false);
  const [manual, setManual] = useState('');
  const [found, setFound] = useState<{ phone: string; user: UserSummary }[]>();
  const [matched, setMatched] = useState<number>();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>();
  const [scanned, setScanned] = useState<{ unique: number; entries: number }>();
  const [error, setError] = useState<string>();

  /** Closing the dialog stops an in-flight lookup rather than orphaning it. */
  const abortLookup = useRef<{ aborted: boolean } | undefined>(undefined);
  useEffect(() => () => {
    if (abortLookup.current) abortLookup.current.aborted = true;
  }, []);

  /** Coded errors get real copy; anything else falls back to its own message. */
  function describe(err: unknown): string {
    if (err instanceof InvalidPhoneError) {
      return `${t('phone.invalid')} ${t('phone.invalidHint')}`;
    }
    return (err as Error).message;
  }

  async function publish() {
    setBusy(true);
    setError(undefined);
    try {
      await client.setDiscoverablePhone(myPhone.trim() || null);
      setSaved(true);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function lookup(numbers: string[]) {
    setBusy(true);
    setError(undefined);
    setProgress(undefined);
    setScanned(undefined);

    // 20,000 hash rounds per number, on this thread. A whole address book runs
    // for tens of seconds, so it has to report progress and it has to stop when
    // the dialog closes.
    const signal = { aborted: false };
    abortLookup.current = signal;

    try {
      const users = await client.findContactsByPhone(numbers, {
        signal,
        onScanned: (unique, entries) => setScanned({ unique, entries }),
        onProgress: (done, total) => setProgress({ done, total }),
      });
      if (signal.aborted) return undefined;
      setFound(users);
      return users;
    } catch (err) {
      setError(describe(err));
      return undefined;
    } finally {
      setBusy(false);
      setProgress(undefined);
    }
  }

  /** Read the address book, if the browser has the API and the user allows it. */
  async function fromContacts() {
    try {
      const picker = (navigator as unknown as {
        contacts: { select: (props: string[], opts: { multiple: boolean }) => Promise<{ tel?: string[] }[]> };
      }).contacts;
      const picked = await picker.select(['tel'], { multiple: true });
      const numbers = picked.flatMap((c) => c.tel ?? []);
      if (numbers.length === 0) return;
      const users = await lookup(numbers);
      setMatched(users?.length);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function start(user: UserSummary) {
    const conversation = await client.startDirectConversation(user);
    onOpened(conversation.id);
  }

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" role="dialog" aria-modal="true" aria-label={t('phone.title')}>
        <header className="dialog__header">
          <h2>{t('phone.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('invite.close')}>
            ✕
          </button>
        </header>

        <p className="hint">
          {t('phone.privacy')}
        </p>

        <PhoneField
          label={t('phone.mine')}
          onChange={(composed) => {
            setMyPhone(composed);
            setSaved(false);
          }}
        />
        <button className="primary" onClick={() => void publish()} disabled={busy}>
          {saved ? `✓ ${t('phone.saved')}` : t('phone.save')}
        </button>

        <hr className="dialogRule" />

        <label>{t('phone.findFriends')}</label>
        {contactPickerAvailable() ? (
          <>
            <button className="ghost" onClick={() => void fromContacts()} disabled={busy}>
              📇 {t('phone.fromContacts')}
            </button>
            {/* Same reassurance the mobile sheet gives: what leaves the device
                is hashes, never the address book. */}
            <p className="hint">{t('phone.contactsPrivacy')}</p>
            {/* A count, not a bare spinner: the hashing is deliberately slow and
                silence for that long reads as a hang. */}
            {busy && (
              <p className="hint">
                {progress
                  ? t('phone.importingProgress', { done: progress.done, total: progress.total })
                  : scanned
                    ? t('phone.importing')
                    : t('phone.importScanning')}
              </p>
            )}
            {busy && scanned && (
              <p className="hint">
                {t('phone.importScanned', { entries: scanned.entries, unique: scanned.unique })}
              </p>
            )}
            {matched !== undefined && <p className="hint">{t('phone.matched', { count: matched })}</p>}
          </>
        ) : (
          <p className="hint">
            {t('phone.noContactPicker')}
          </p>
        )}

        <PhoneField label={t('phone.checkOne')} onChange={setManual} />
        <button
          className="ghost"
          onClick={() => void lookup([manual])}
          disabled={busy || manual.trim().length < 6}
        >
          {t('phone.search')}
        </button>

        {error && <p className="error">{error}</p>}

        {found && (
          <div className="results">
            {found.length === 0 && <p className="muted">{t('phone.noneRegistered')}</p>}
            {found.map(({ phone, user }) => (
              <button key={user.id} className="result" onClick={() => void start(user)}>
                <Avatar name={user.displayName} size={38} />
                <span>
                  <strong>{user.displayName}</strong>
                  <small className="muted" dir="ltr">
                    {phone}
                  </small>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
